import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import {
  Customer,
  Phone,
  EmailAddress,
  District,
} from "../../../../core/api/models";
import CustomerPhone from "../../../../components/customer-phone";
import CustomerEmail from "../../../../components/customer-email";
import Countries from "../../../../utils/countries.json";
import title from "../../../../utils/title.json";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../../api";
import {
  CheckPhoneNumberEmpty,
  Validate965DigitPhoneNumber,
  ValidateAlphabets,
  ValidateEmail,
} from "../../../../utils/Validatation";
import { useDataLoader } from "react-remote-data-hooks";
import validator from "validator";

const AddressType = [
  { name: "Home", value: "home" },
  { name: "Work", value: "work" },
];

const ContactDetails: React.FunctionComponent<{
  error?: string;
  updateUser: () => void;
  customerDetails?: Customer;
  fields: { displayName: string; value: string; type: string; group: string }[];
}> = (props) => {
  const [isAddressSelected, setAddressSelected] = useState<boolean>(false);
  const [isAddressPrimary, setAddressPrimary] = useState<boolean>(false);

  const [isEmailEnable, setIsEmailEnable] = useState<boolean>(true);

  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

  const [selectedPhone, setSelectedPhone] = useState<Phone[] | undefined>();
  const [selectedEmail, setSelectedEmail] = useState<
    EmailAddress[] | undefined
  >();

  const {
    control,
    handleSubmit,
    errors,
    clearErrors,
    watch,
    setValue,
  } = useForm<Customer>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const { data: districtList } = useDataLoader<District[]>(() =>
    api.settings.getDistricts()
  );

  const governorates = [
    ...new Set(districtList?.map((item) => item.governorate)),
  ].sort();

  const governorate = watch("governorate");

  const districts = districtList
    ? !governorate
      ? [...(districtList?.map((item) => item.name) || []), "Other"]
      : [
          ...districtList
            ?.filter((vv) => vv.governorate === governorate)
            ?.map((item) => item.name),
          "Other",
        ] || []
    : [];

  const district = watch("district");

  const setAsPrimary = () => {
    setAddressPrimary(!isAddressPrimary);
  };

  const onSubmit = async (data?: any) => {
    setIsSubmiting(true);

    const updateData: Customer = {
      prefix: data.prefix || props.customerDetails?.prefix,
      firstName: data.firstName || props.customerDetails?.firstName,
      lastName: data.lastName || props.customerDetails?.lastName,
    };

    props?.fields?.forEach((p) => {
      if (p.value === "primaryPhone") {
        updateData.phones = selectedPhone?.filter((x) =>
          CheckPhoneNumberEmpty(x.phone)
        );
      } else if (p.value === "primaryEmail") {
        updateData.emails = selectedEmail?.filter((x) =>
          ValidateEmail(x.email)
        );
      } else if (p.value === "identifiers.civilId") {
        updateData.civilId = props.customerDetails?.isCompanyCustomer
          ? `000000${data.identifiers.civilId}`
          : data.identifiers.civilId;
      } else {
        (updateData as any)[p.value] = data[p.value];
      }
    });

    if (isAddressSelected && checkAddrsssIsValid(data)) {
      updateData.addresses = [
        {
          houseName: data?.houseName,
          houseNumber: data?.houseNumber,
          address: data?.address,
          unitType: data?.unitType,
          block: data?.block,
          street: data?.street,
          governorate: data?.governorate,
          postalCode: data?.postalCode,
          country: data?.country,
          addressType: data?.addressType?.value,
          isPrimary: isAddressPrimary,
          isOtherDistrict: !!(data.district === "Other"),
          district:
            data?.district === "Other" ? data.otherDistrict : data?.district,
        },
      ];
    }
    try {
      const response = await api.customer.updateCustomer(
        updateData,
        props.customerDetails?._id
      );
      props?.updateUser();
      toastr.success("Success", "Customer added successfully");
    } catch (error) {
      toastr.error("Error", error?.response?.data?.message);
    }
    setIsSubmiting(true);
  };

  const checkAddrsssIsValid = (data?: Customer) => {
    return (
      data?.houseName ||
      data?.houseNumber ||
      data?.address ||
      data?.unitType ||
      data?.block ||
      data?.street ||
      data?.governorate ||
      data?.postalCode
    );
  };

  const onChangePhone = (phoneNumber: Phone[]) => {
    setSelectedPhone(phoneNumber);
  };

  const onChangeEmail = (emailAdd: EmailAddress[]) => {
    setSelectedEmail(emailAdd);
  };

  return (
    <div className="pl-4 p-3 accordion__body border-bottom">
      <form onSubmit={handleSubmit(onSubmit)}>
        {!!props?.fields?.filter((x) => x.value === "prefix")?.length &&
          !props.customerDetails?.prefix && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Title
              </label>
              <Controller
                control={control}
                name="prefix"
                defaultValue={props.customerDetails?.prefix}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={title}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select prefix"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                  />
                )}
              />
              {errors.prefix && <ErrorMessage>Prefix is required</ErrorMessage>}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "firstName")?.length &&
          !props.customerDetails?.firstName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                First Name<span className="text-danger">*</span>
              </label>

              <Controller
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    return !!value.trim();
                  },
                  maxLength: 25,
                }}
                defaultValue={props.customerDetails?.firstName}
                name="firstName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="First Name"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="firstName"
                  />
                )}
              />
              {errors.firstName && errors.firstName.type === "required" && (
                <ErrorMessage>First Name is required</ErrorMessage>
              )}
              {errors.firstName && errors.firstName.type === "maxLength" && (
                <ErrorMessage>
                  First name should contains maximum 25 characters
                </ErrorMessage>
              )}
            </div>
          )}

        {!!props?.fields?.filter((x) => x.value === "arabicFirstName")
          ?.length &&
          !props.customerDetails?.arabicFirstName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                First Name In Arabic
              </label>

              <Controller
                control={control}
                defaultValue={props.customerDetails?.arabicFirstName}
                name="arabicFirstName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="First Name In Arabic"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="arabicFirstName"
                  />
                )}
              />
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "middleName")?.length &&
          !props.customerDetails?.middleName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Middle Name
              </label>
              <Controller
                control={control}
                defaultValue={props.customerDetails?.middleName}
                name="middleName"
                rules={{
                  required: !props.customerDetails?.identifiers?.magicNumber,
                  validate: (value) => {
                    return value ? !!ValidateAlphabets(value) : true;
                  },
                  maxLength: 25,
                }}
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Middle Name"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="middleName"
                  />
                )}
              />
              {errors.middleName && errors.middleName.type === "required" && (
                <ErrorMessage>Middle Name is required</ErrorMessage>
              )}
              {errors.middleName && errors.middleName.type === "maxLength" && (
                <ErrorMessage>
                  Middle name should contains maximum 25 characters
                </ErrorMessage>
              )}
              {errors.middleName && errors.middleName.type === "validate" && (
                <ErrorMessage>Invalid Middle Name</ErrorMessage>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "arabicMiddleName")
          ?.length &&
          !props.customerDetails?.arabicMiddleName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Middle Name In Arabic
              </label>
              <Controller
                control={control}
                defaultValue={props.customerDetails?.arabicMiddleName}
                name="arabicMiddleName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Middle Name In Arabic"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="arabicMiddleName"
                  />
                )}
              />
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "lastName")?.length &&
          !props.customerDetails?.lastName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Last Name<span className="text-danger">*</span>
              </label>
              <Controller
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    return !!value.trim();
                  },
                  maxLength: 25,
                }}
                defaultValue={props.customerDetails?.lastName}
                name="lastName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Last Name"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="lastName"
                  />
                )}
              />
              {errors.lastName && errors.lastName.type === "required" && (
                <ErrorMessage>Last Name is required</ErrorMessage>
              )}
              {errors.lastName && errors.lastName.type === "maxLength" && (
                <ErrorMessage>
                  Last name should contains maximum 25 characters
                </ErrorMessage>
              )}
            </div>
          )}

        {!!props?.fields?.filter((x) => x.value === "arabicLastName")?.length &&
          !props.customerDetails?.arabicLastName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Last Name In Arabic
              </label>

              <Controller
                control={control}
                defaultValue={props.customerDetails?.arabicLastName}
                name="arabicLastName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder=" Last Name In Arabic"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="arabicLastName"
                  />
                )}
              />
            </div>
          )}

        {!!props?.fields?.filter((x) => x.value === "secondSurname")?.length &&
          !props.customerDetails?.secondSurname && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Second Surname
              </label>
              <Controller
                control={control}
                defaultValue={props.customerDetails?.secondSurname}
                name="secondSurname"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Second Surname"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="secondSurname"
                  />
                )}
              />
              {errors.secondSurname &&
                errors.secondSurname.type === "required" && (
                  <ErrorMessage>Second Surname is required</ErrorMessage>
                )}
              {errors.secondSurname &&
                errors.secondSurname.type === "maxLength" && (
                  <ErrorMessage>
                    Second Surname should contains maximum 25 characters
                  </ErrorMessage>
                )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "middleParts")?.length &&
          !props.customerDetails?.middleParts && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Middle Parts
              </label>
              <Controller
                control={control}
                defaultValue={props.customerDetails?.middleParts}
                name="middleParts"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Middle Parts"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="middleParts"
                  />
                )}
              />
              {errors.middleParts && errors.middleParts.type === "required" && (
                <ErrorMessage>Middle Parts is required</ErrorMessage>
              )}
              {errors.middleParts &&
                errors.middleParts.type === "maxLength" && (
                  <ErrorMessage>
                    Middle Parts should contains maximum 25 characters
                  </ErrorMessage>
                )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "shortName")?.length &&
          !props.customerDetails?.shortName && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Short Name
              </label>
              <Controller
                control={control}
                defaultValue={props.customerDetails?.shortName}
                name="shortName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Short Name"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="shortName"
                  />
                )}
              />
              {errors.shortName && errors.shortName.type === "required" && (
                <ErrorMessage>Short Name is required</ErrorMessage>
              )}
              {errors.shortName && errors.shortName.type === "maxLength" && (
                <ErrorMessage>
                  Short Name should contains maximum 25 characters
                </ErrorMessage>
              )}
            </div>
          )}

        {!!props?.fields?.filter((x) => x.value === "primaryPhone")?.length &&
          !props.customerDetails?.primaryPhone && (
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Phone<span className="text-danger">*</span>
              </label>

              <Controller
                control={control}
                rules={{
                  required: true,
                  validate: (value) => {
                    return !(
                      !selectedPhone ||
                      (selectedPhone &&
                        selectedPhone?.filter((x) =>
                          Validate965DigitPhoneNumber(x.phone)
                        ).length !== selectedPhone?.length)
                    );
                  },
                }}
                name="phones"
                defaultValue={props.customerDetails?.phones}
                render={({ onChange, value, name }) => (
                  <CustomerPhone
                    className="form-control p-0 rounded-sm font-weight-semibold"
                    placeholder="Phone"
                    value={selectedPhone}
                    onChange={(val) => {
                      onChangePhone(val);
                      onChange(val);
                    }}
                  />
                )}
              />
              {errors.phones && (
                <div>
                  {!selectedPhone ? (
                    <ErrorMessage>Valid Phone is required</ErrorMessage>
                  ) : (
                    <>
                      {selectedPhone?.filter(
                        (x) =>
                          !!(
                            x.phone.startsWith("91") ||
                            x.phone.startsWith("+91")
                          )
                      ).length > 0 ? (
                        <ErrorMessage>
                          Phone number should be 10 digits
                        </ErrorMessage>
                      ) : (
                        <ErrorMessage>Invalid Phone number</ErrorMessage>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "primaryEmail")?.length &&
          !props.customerDetails?.primaryEmail && (
            <div className="form-group font-weight-semibold mb-4">
              <label
                htmlFor=""
                className="font-sm text-uppercase d-flex align-items-end"
              >
                Email
                <div className="ml-auto switch switch--sm">
                  <input
                    type="checkbox"
                    name=""
                    onChange={(val) => {
                      setIsEmailEnable(!isEmailEnable);
                      if (isEmailEnable) {
                        clearErrors("emails");
                      }
                    }}
                    id="emailSwitch"
                    checked={isEmailEnable}
                  />
                  <label htmlFor="emailSwitch">
                    <div className="switch-handle mr-2"></div>
                  </label>
                </div>
              </label>
              <Controller
                control={control}
                rules={{
                  required:
                    (!selectedEmail ||
                      (selectedEmail &&
                        selectedEmail?.filter((x) => ValidateEmail(x.email))
                          .length === 0)) &&
                    isEmailEnable,
                }}
                name="emails"
                defaultValue={props.customerDetails?.emails}
                render={({ onChange, value, name }) => (
                  <CustomerEmail
                    className="form-control p-0 rounded-sm font-weight-semibold"
                    placeholder="Email"
                    value={selectedEmail}
                    onChange={onChangeEmail}
                    disabled={!isEmailEnable}
                  />
                )}
              />
              {errors.emails && (
                <div>
                  <ErrorMessage>Email is required</ErrorMessage>
                </div>
              )}
            </div>
          )}
        {!!props?.fields?.filter((x) => x.value === "addresses")?.length &&
          !props.customerDetails?.addresses?.length && (
            <div className="mb-4">
              <h5 className="text-uppercase font-weight-semibold mb-2">
                Address
              </h5>
              {!isAddressSelected && (
                <button
                  type="button"
                  className="btn btn-link font-sm p-0 font-weight-semibold"
                  onClick={() => setAddressSelected(true)}
                >
                  + add address
                </button>
              )}
            </div>
          )}

        {isAddressSelected && (
          <div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                House Name
              </label>
              <Controller
                control={control}
                // rules={{ required: isAddressSelected }}
                name="houseName"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="House Name"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="houseName"
                  />
                )}
              />
              {errors.houseName && (
                <ErrorMessage>
                  {errors.houseName.type === "required"
                    ? "House Name is required"
                    : "Invalid House Name"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                House Number
              </label>
              <Controller
                control={control}
                rules={{
                  required: isAddressSelected,
                  validate: (value) => {
                    return !!value.trim();
                  },
                }}
                name="houseNumber"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="House Number"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="houseNumber"
                  />
                )}
              />
              {errors.houseNumber && (
                <ErrorMessage>
                  {errors.houseNumber.type === "required"
                    ? "House Number is required"
                    : "Invalid House Number"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Building Address
              </label>
              <Controller
                control={control}
                rules={{
                  required: isAddressSelected,
                  validate: (value) => {
                    return !!value.trim();
                  },
                }}
                name="address"
                defaultValue={props.customerDetails?.address}
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Building Address"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="address"
                  />
                )}
              />
              {errors.address && (
                <ErrorMessage>
                  {errors.address.type === "required"
                    ? "Building Address is required"
                    : "Invalid Building Address"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Unit Type
              </label>
              <Controller
                control={control}
                name="unitType"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Unit Type"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="unitType"
                  />
                )}
              />
              {errors.unitType && (
                <ErrorMessage>
                  {errors.unitType.type === "required"
                    ? "Unit Type is required"
                    : "Invalid Unit Type"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Block
              </label>
              <Controller
                control={control}
                name="block"
                rules={{
                  required: isAddressSelected,
                  validate: (value) => {
                    return !!value.trim();
                  },
                }}
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Block"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="block"
                  />
                )}
              />
              {errors.block && (
                <ErrorMessage>
                  {errors.block.type === "required"
                    ? "Block is required"
                    : "Invalid Block"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Street
              </label>
              <Controller
                control={control}
                rules={{
                  required: isAddressSelected,
                  validate: (value) => {
                    return !!value.trim();
                  },
                }}
                name="street"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Street"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="street"
                  />
                )}
              />
              {errors.street && (
                <ErrorMessage>
                  {errors.street.type === "required"
                    ? "Street is required"
                    : "Invalid Street"}
                </ErrorMessage>
              )}
            </div>

            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Governorate
              </label>
              <Controller
                control={control}
                name="governorate"
                rules={{ required: isAddressSelected }}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={governorates?.map((key) => {
                      return {
                        name: key,
                      };
                    })}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select Governorate"}
                    onChange={(val) => {
                      onChange(val.name);
                      setValue("district", "");
                      setValue("otherDistrict", "");
                    }}
                  />
                )}
              />
              {errors.governorate && (
                <ErrorMessage>
                  {errors.governorate.type === "required"
                    ? "Governorate is required"
                    : "Invalid Governorate"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                District
              </label>
              <Controller
                control={control}
                name="district"
                rules={{ required: isAddressSelected }}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={districts?.map((key) => {
                      return {
                        name: key,
                      };
                    })}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select District"}
                    onChange={(val) => {
                      onChange(val.name);
                      setValue("otherDistrict", "");
                    }}
                  />
                )}
              />
              {errors.district && (
                <ErrorMessage>
                  {errors.district.type === "required"
                    ? "District is required"
                    : "Invalid District"}
                </ErrorMessage>
              )}
            </div>
            {district === "Other" && (
              <div className="form-group font-weight-semibold mb-4">
                <label htmlFor="" className="font-sm text-uppercase"></label>
                <Controller
                  control={control}
                  name="otherDistrict"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter District"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="otherDistrict"
                    />
                  )}
                />
                {errors.district && (
                  <ErrorMessage>
                    {errors.district.type === "required"
                      ? "District is required"
                      : "Invalid District"}
                  </ErrorMessage>
                )}
              </div>
            )}
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Postal Code
              </label>
              <Controller
                control={control}
                // rules={{ required: isAddressSelected }}
                name="postalCode"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Postal Code"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="postalCode"
                  />
                )}
              />
              {errors.postalCode && (
                <ErrorMessage>
                  {errors.postalCode.type === "required"
                    ? "Postal Code is required"
                    : "Invalid Postal Code"}
                </ErrorMessage>
              )}
            </div>
            <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                Country
              </label>
              <Controller
                control={control}
                // rules={{ required: isAddressSelected }}
                name="country"
                defaultValue="Kuwait"
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={Countries}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select country"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                    filter={"contains"}
                  />
                )}
              />
              {errors.country && (
                <ErrorMessage>
                  {errors.country.type === "required"
                    ? "Country is required"
                    : "Invalid Country"}
                </ErrorMessage>
              )}
            </div>
            {/* <div className="form-group font-weight-semibold mb-4">
              <label htmlFor="" className="font-sm text-uppercase">
                PACI Number
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="paciNumber"
                defaultValue={props.customerDetails?.paciNumber}
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="PACI Number"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="paciNumber"
                  />
                )}
              />
              {errors.paciNumber && (
                <ErrorMessage>
                  {errors.paciNumber.type === 'required'
                    ? 'PACI Number is required'
                    : 'Invalid PACI Number'}
                </ErrorMessage>
              )}
            </div> */}
          </div>
        )}
        <div className="form-group pt-2">
          <button
            disabled={isSubmiting}
            className="btn btn-primary btn-block font-normal font-weight-bold"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactDetails;
